import React, { useState, useEffect, useCallback } from 'react';
import { AnalyticsApi } from '../../services/api/AnalyticsApi';
import './RetentionDashboard.css';

const RetentionDashboard = () => {
  const [retentionData, setRetentionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Configuration state
  const [cohortType, setCohortType] = useState('SIGNUP');
  const [period, setPeriod] = useState('WEEKLY');
  const [returnMetric, setReturnMetric] = useState('ANY_ACTIVITY');
  const [maxPeriods, setMaxPeriods] = useState(12);
  const [dateRange, setDateRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 6); // Last 6 months
    return { from: from.toISOString(), to: to.toISOString() };
  });

  // Fetch retention data
  const fetchRetention = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const request = {
        cohortType,
        period,
        returnMetric,
        maxPeriods,
        from: dateRange.from,
        to: dateRange.to
      };
      
      const data = await AnalyticsApi.getCohortRetention(request);
      setRetentionData(data);
    } catch (err) {
      console.error('Failed to fetch retention data:', err);
      setError(err.message || 'Failed to load retention data');
    } finally {
      setLoading(false);
    }
  }, [cohortType, period, returnMetric, maxPeriods, dateRange]);

  // Auto-fetch on configuration change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRetention();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [fetchRetention]);

  // Get CSS class for retention rate (PostHog-style blue gradient)
  const getRetentionColor = (rate) => {
    if (rate >= 80) return 'retention-blue-900';
    if (rate >= 60) return 'retention-blue-700';
    if (rate >= 40) return 'retention-blue-500';
    if (rate >= 25) return 'retention-blue-400';
    if (rate >= 15) return 'retention-blue-300';
    if (rate >= 8) return 'retention-blue-200';
    return 'retention-blue-100';
  };

  // Format cohort label — e.g. "2026-W03" → "Jan 15", "2026-01-20" → "Jan 20"
  const formatCohortLabel = (cohortId) => {
    if (!cohortId) return cohortId;
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // ISO week format "YYYY-Www"
    const weekMatch = cohortId.match(/^(\d{4})-W(\d{2})$/);
    if (weekMatch) {
      const [, yearStr, weekStr] = weekMatch;
      const year = parseInt(yearStr, 10);
      const week = parseInt(weekStr, 10);
      // ISO week-to-date: Jan 4 is always in week 1
      const jan4 = new Date(year, 0, 4);
      const dayOfWeek = jan4.getDay() || 7; // Mon=1..Sun=7
      const weekStart = new Date(jan4);
      weekStart.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
      return `${MONTHS[weekStart.getMonth()]} ${weekStart.getDate()}`;
    }

    // ISO date format "YYYY-MM-DD"
    const dateMatch = cohortId.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      const month = parseInt(dateMatch[2], 10) - 1;
      const day = parseInt(dateMatch[3], 10);
      return `${MONTHS[month]} ${day}`;
    }

    // Monthly format "YYYY-MM"
    const monthMatch = cohortId.match(/^(\d{4})-(\d{2})$/);
    if (monthMatch) {
      const month = parseInt(monthMatch[2], 10) - 1;
      return MONTHS[month];
    }

    return cohortId;
  };

  // Calculate max period number across all cohorts
  const getMaxPeriodInData = () => {
    if (!retentionData?.cohorts?.length) return 0;
    return Math.max(...retentionData.cohorts.map(c => 
      Math.max(0, ...Object.keys(c.retention || {}).map(Number))
    ));
  };

  // Get period prefix label based on granularity
  const getPeriodPrefix = () => {
    if (period === 'DAILY') return 'Day';
    if (period === 'MONTHLY') return 'Month';
    return 'Week';
  };

  return (
    <div className="retention-dashboard">
      <div className="dashboard-header">
        <div className="dashboard-header-badge">RETENTION • {period === 'DAILY' ? 'DAILY' : period === 'WEEKLY' ? 'WEEKLY' : 'MONTHLY'}</div>
        <h1>New User Retention</h1>
      </div>

      {/* Configuration Panel */}
      <div className="retention-config">
        <div className="config-section">
          <h3>Cohort Definition</h3>
          <div className="config-grid">
            <div className="config-field">
              <label>Cohort Type</label>
              <select 
                value={cohortType} 
                onChange={(e) => setCohortType(e.target.value)}
                className="config-select"
              >
                <option value="SIGNUP">User Signup</option>
                <option value="FIRST_LOGIN">First Login</option>
                <option value="FIRST_PURCHASE">First Purchase</option>
                <option value="FIRST_COURSE">First Course</option>
              </select>
            </div>

            <div className="config-field">
              <label>Period Granularity</label>
              <select 
                value={period} 
                onChange={(e) => setPeriod(e.target.value)}
                className="config-select"
              >
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>

            <div className="config-field">
              <label>Return Activity</label>
              <select 
                value={returnMetric} 
                onChange={(e) => setReturnMetric(e.target.value)}
                className="config-select"
              >
                <option value="ANY_ACTIVITY">Any Activity</option>
                <option value="LOGIN">Login Events</option>
                <option value="ENGAGEMENT">Content Engagement</option>
                <option value="PURCHASE">Purchase Events</option>
              </select>
            </div>

            <div className="config-field">
              <label>Max Periods to Track</label>
              <input 
                type="number" 
                value={maxPeriods} 
                onChange={(e) => setMaxPeriods(parseInt(e.target.value))}
                min="1"
                max="52"
                className="config-input"
              />
            </div>
          </div>
        </div>

        <div className="config-section">
          <h3>Date Range</h3>
          <div className="config-grid">
            <div className="config-field">
              <label>From</label>
              <input 
                type="date" 
                value={dateRange.from.split('T')[0]} 
                onChange={(e) => setDateRange(prev => ({ ...prev, from: new Date(e.target.value).toISOString() }))}
                className="config-input"
              />
            </div>
            <div className="config-field">
              <label>To</label>
              <input 
                type="date" 
                value={dateRange.to.split('T')[0]} 
                onChange={(e) => setDateRange(prev => ({ ...prev, to: new Date(e.target.value).toISOString() }))}
                className="config-input"
              />
            </div>
          </div>
        </div>

        <button 
          onClick={fetchRetention} 
          disabled={loading}
          className="refresh-button"
        >
          {loading ? 'Loading...' : 'Refresh Analysis'}
        </button>
      </div>

      {/* Loading/Error States */}
      {loading && <div className="loading-spinner">Analyzing cohort retention...</div>}
      {error && <div className="error-message">{error}</div>}

      {/* Results */}
      {retentionData && !loading && (
        <>
          {/* Summary Metrics */}
          <div className="retention-summary">
            <div className="summary-card">
              <div className="summary-label">Total Cohorts</div>
              <div className="summary-value">{retentionData.metadata?.totalCohorts || 0}</div>
            </div>
            
            {retentionData.averageRetention && Object.keys(retentionData.averageRetention).length > 0 && (
              <>
                <div className="summary-card">
                  <div className="summary-label">Avg {getPeriodPrefix()} 1 Retention</div>
                  <div className="summary-value">
                    {retentionData.averageRetention[1]?.toFixed(1) || 'N/A'}%
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Avg {getPeriodPrefix()} 7 Retention</div>
                  <div className="summary-value">
                    {retentionData.averageRetention[7]?.toFixed(1) || 'N/A'}%
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Avg {getPeriodPrefix()} 30 Retention</div>
                  <div className="summary-value">
                    {retentionData.averageRetention[30]?.toFixed(1) || 
                     retentionData.averageRetention[4]?.toFixed(1) || 'N/A'}%
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Retention Heatmap */}
          {retentionData.cohorts && retentionData.cohorts.length > 0 ? (
            <div className="retention-heatmap-container">
              <h3>Retention Heatmap</h3>
              <div className="heatmap-scroll">
                <table className="retention-heatmap">
                  <thead>
                    <tr>
                      <th className="cohort-header">Cohort</th>
                      <th className="size-header">Size</th>
                      {Array.from({ length: getMaxPeriodInData() + 1 }, (_, i) => (
                        <th key={i} className="period-header">
                          {`${getPeriodPrefix()} ${i}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {retentionData.cohorts.map((cohort, idx) => {
                      const cohortPeriodCount = Math.max(0, ...Object.keys(cohort.retention || {}).map(Number));
                      return (
                        <tr key={idx} className="cohort-row">
                          <td className="cohort-cell">
                            {formatCohortLabel(cohort.cohortId)}
                          </td>
                          <td className="size-cell">{cohort.cohortSize?.toLocaleString()}</td>
                          {Array.from({ length: getMaxPeriodInData() + 1 }, (_, periodNum) => {
                            if (periodNum > cohortPeriodCount) {
                              return <td key={periodNum} className="retention-cell retention-empty" />;
                            }
                            const retentionPeriod = cohort.retention?.[periodNum];
                            const rate = retentionPeriod?.retentionRate || 0;
                            const activeUsers = retentionPeriod?.activeUsers || 0;
                            
                            return (
                              <td 
                                key={periodNum} 
                                className={`retention-cell ${getRetentionColor(rate)}`}
                                title={`${activeUsers.toLocaleString()} of ${cohort.cohortSize?.toLocaleString()} users (${rate.toFixed(1)}%)`}
                              >
                                <div className="retention-rate">{rate.toFixed(1)}%</div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="no-data-message">
              No cohort data available for the selected period. Try adjusting the date range or cohort type.
            </div>
          )}

          {/* Average Retention Trend */}
          {retentionData.averageRetention && Object.keys(retentionData.averageRetention).length > 0 && (
            <div className="average-retention-trend">
              <h3>Average Retention Curve</h3>
              <div className="trend-chart">
                {Object.entries(retentionData.averageRetention)
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([periodNum, avgRate]) => (
                    <div key={periodNum} className="trend-bar-container">
                      <div className="trend-label">
                        {period === 'DAILY' ? `D${periodNum}` : 
                         period === 'WEEKLY' ? `W${periodNum}` : `M${periodNum}`}
                      </div>
                      <div className="trend-bar-wrapper">
                        <div 
                          className={`trend-bar ${getRetentionColor(avgRate)}`}
                          style={{ width: `${avgRate}%` }}
                        >
                          <span className="trend-value">{avgRate.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RetentionDashboard;
